import { useState } from 'react'
import { RiUploadCloud2Line, RiCloseLine } from 'react-icons/ri'
import { Btn, Input, Textarea, Field, Toggle, Modal, Label } from '../../../components/ui/design.jsx'
import { CATEGORIES_SUGGEST, kategoriColor, parseKat } from '../constants.js'
import StarRating from './StarRating.jsx'

export default function KelasModal({ item, onClose, onSave }) {
  const isNew = !item
  const [form, setForm] = useState({
    title:        item?.title        || '',
    deskripsi:    item?.deskripsi    || '',
    kategori:     parseKat(item?.kategori),
    rating:       item?.rating       ?? 5.0,
    link_daftar:  item?.link_daftar  || '',
    is_active:    item?.is_active    ?? true,
    sort_order:   item?.sort_order   ?? 0,
    image:        null,
    image_preview: item?.thumb_url || item?.image_url || '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  const addFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return
    // Revoke previous blob to prevent memory leak
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview)
    setForm((f) => ({ ...f, image: file, image_preview: URL.createObjectURL(file) }))
  }

  const clearImage = (e) => {
    e.stopPropagation()
    if (form.image_preview?.startsWith('blob:')) URL.revokeObjectURL(form.image_preview)
    setForm((f) => ({ ...f, image: null, image_preview: '' }))
  }

  const toggleKategori = (kat) =>
    setForm((f) => ({
      ...f,
      kategori: f.kategori.includes(kat)
        ? f.kategori.filter((k) => k !== kat)
        : [...f.kategori, kat],
    }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.title.trim()) return
    setSubmitting(true)
    try {
      await onSave({ ...form, isNew, id: item?.id })
    } finally { setSubmitting(false) }
  }

  return (
    <Modal onClose={onClose} title={isNew ? 'Tambah Kelas Baru' : `Edit "${item.title}"`} wide>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nama Kelas" req>
          <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="cth: Kelas Basic N4" />
        </Field>

        {/* Multi-select kategori */}
        <div>
          <Label>Kategori (bisa lebih dari satu)</Label>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES_SUGGEST.map((c) => {
              const sel = form.kategori.includes(c)
              return (
                <button key={c} type="button" onClick={() => toggleKategori(c)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    sel ? `${kategoriColor(c)} border-transparent` : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}>
                  {sel && '✓ '}{c}
                </button>
              )
            })}
          </div>
          {form.kategori.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {form.kategori.map((k) => (
                <span key={k} className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${kategoriColor(k)}`}>
                  {k}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, kategori: f.kategori.filter((x) => x !== k) }))} className="hover:text-red-600">
                    <RiCloseLine className="text-xs" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Cover image drop zone */}
        <Field label="Gambar Cover">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); addFile(e.dataTransfer.files[0]) }}
            onClick={() => document.getElementById('kelas-file-input')?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-all ${
              dragOver ? 'border-blue-400 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <input id="kelas-file-input" type="file" accept="image/*" onChange={(e) => addFile(e.target.files?.[0])} className="sr-only" />
            {form.image_preview ? (
              <div className="relative group">
                <img src={form.image_preview} alt="Preview" className="mx-auto max-h-48 rounded-lg object-contain" />
                <button type="button" onClick={clearImage}
                  className="absolute right-1 top-1 rounded-lg bg-black/50 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <RiCloseLine />
                </button>
              </div>
            ) : (
              <>
                <RiUploadCloud2Line className="mb-2 text-3xl text-gray-300" />
                <p className="text-sm font-semibold text-gray-700">Seret gambar ke sini atau klik untuk memilih</p>
                <p className="mt-1 text-xs text-gray-400">jpg, png, webp — maks 8 MB</p>
              </>
            )}
          </div>
        </Field>

        <Field label="Deskripsi">
          <Textarea rows={3} value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} placeholder="Penjelasan tentang kelas ini" />
        </Field>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Rating">
            <div className="flex items-center h-9">
              <StarRating value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
            </div>
          </Field>
          <Field label="Link Daftar">
            <Input value={form.link_daftar} onChange={(e) => setForm({ ...form, link_daftar: e.target.value })} placeholder="https://..." />
          </Field>
          <Field label="Urutan">
            <Input type="number" min={0} value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          </Field>
        </div>

        <div className="flex items-center gap-2.5">
          <Toggle checked={form.is_active} onChange={(v) => setForm({ ...form, is_active: v })} />
          <span className="text-sm text-gray-700">Kelas aktif</span>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Btn type="button" variant="secondary" onClick={onClose} disabled={submitting}>Batal</Btn>
          <Btn type="submit" disabled={submitting}>{submitting ? 'Menyimpan…' : isNew ? 'Buat Kelas' : 'Simpan'}</Btn>
        </div>
      </form>
    </Modal>
  )
}
